// web/src/components/IgnitionOverlay.tsx
// The dramatic completion transition, mounted once at the app root (survives the
// Processing → Player route swap). On ignitionFx.trigger() it detonates a white-cyan IRIS
// from the blast origin (an explosion), navigates to the target UNDER the full-white flash,
// then FADES the white out to reveal the Player. A shockwave ring races ahead of the fill.
// Reduced-motion → a quick white cross-fade. pointer-events-none: never blocks the UI.
import { useEffect } from "react";
import type { JSX } from "react";
import { useNavigate } from "react-router-dom";
import { motion, useMotionValue, useMotionTemplate, animate, useReducedMotion } from "motion/react";
import { useIgnitionFx, IGNITION_TIMING } from "@/lib/ignitionFx";
import { playIgnition } from "@/lib/sfx";

const EXPLODE_EASE = [0.16, 1, 0.3, 1] as const; // fast-out — reads as a detonation

export function IgnitionOverlay(): JSX.Element {
  const st = useIgnitionFx();
  const navigate = useNavigate();
  const reduced = useReducedMotion() ?? false;

  const fillR = useMotionValue(0); // clip-path circle radius (the blast fill)
  const ringScale = useMotionValue(0);
  const ringOpacity = useMotionValue(0);
  const overlayOpacity = useMotionValue(0);

  const clip = useMotionTemplate`circle(${fillR}px at ${st.originX}px ${st.originY}px)`;

  useEffect(() => {
    if (st.nonce === 0) return; // no trigger yet
    const W = window.innerWidth;
    const H = window.innerHeight;
    const { originX, originY, to } = st;
    // farthest corner from the origin (× a hair) so the fill fully covers the viewport
    const maxR = Math.hypot(Math.max(originX, W - originX), Math.max(originY, H - originY)) * 1.08;
    const { expandMs, holdMs, fadeMs } = IGNITION_TIMING;

    playIgnition();
    overlayOpacity.set(1);
    fillR.set(0);
    ringScale.set(0.02);
    ringOpacity.set(0.9);

    if (reduced) {
      fillR.set(maxR);
      if (to) navigate(to);
      const a = animate(overlayOpacity, 0, { duration: 0.4, delay: 0.06 });
      return () => a.stop();
    }

    const a1 = animate(fillR, maxR, { duration: expandMs / 1000, ease: EXPLODE_EASE });
    const a2 = animate(ringScale, (maxR / 60) * 1.15, { duration: (expandMs + 140) / 1000, ease: EXPLODE_EASE });
    const a3 = animate(ringOpacity, 0, { duration: (expandMs + 140) / 1000, ease: "easeOut" });
    // swap the route at the blast peak — hidden under the full-white flash
    const navT = setTimeout(() => { if (to) navigate(to); }, expandMs + 30);
    // hold the white a beat, then fade to reveal the Player underneath
    const a4 = animate(overlayOpacity, 0, { duration: fadeMs / 1000, delay: (expandMs + holdMs) / 1000, ease: "easeInOut" });

    return () => { a1.stop(); a2.stop(); a3.stop(); a4.stop(); clearTimeout(navT); };
  }, [st.nonce]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <motion.div
      aria-hidden
      className="pointer-events-none fixed inset-0 z-[9999]"
      style={{ opacity: overlayOpacity }}
      data-testid="ignition-overlay"
    >
      {/* white-hot core → cyan edge, revealed by the expanding iris */}
      <motion.div
        className="absolute inset-0"
        style={{
          background: `radial-gradient(circle at ${st.originX}px ${st.originY}px, #ffffff 40%, #eaffff 72%, #b0ffff 100%)`,
          clipPath: clip,
          WebkitClipPath: clip,
        }}
      />
      {/* shockwave ring racing outward */}
      <motion.div
        className="absolute rounded-full"
        style={{
          left: st.originX, top: st.originY, width: 120, height: 120, marginLeft: -60, marginTop: -60,
          border: "3px solid rgba(216,255,255,0.9)",
          boxShadow: "0 0 34px 6px rgba(176,255,255,0.7)",
          scale: ringScale,
          opacity: ringOpacity,
        }}
      />
    </motion.div>
  );
}
